import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import type {LabelsCacheData} from './types'
import {updateLabelsCache} from './utils/update-labels-cache'

type UseDeleteLabelOptions = {
  labelId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setIsDialogOpen: (v: boolean) => void
  setErrorsPresent: (v: boolean) => void
}

export function useDeleteLabel({
  labelId,
  repositoryNameWithOwner,
  queryKey,
  setIsDialogOpen,
  setErrorsPresent,
}: UseDeleteLabelOptions) {
  const queryClient = useQueryClient()

  return useCallback(async () => {
    setIsDialogOpen(false)

    // Optimistically remove the label from the TanStack cache
    const previousData = queryKey ? queryClient.getQueryData<LabelsCacheData>(queryKey) : undefined
    if (queryKey) {
      queryClient.setQueryData<LabelsCacheData>(
        queryKey,
        updateLabelsCache(labels => {
          labels.totalCount = (labels.totalCount ?? 0) - 1
          labels.edges = labels.edges.filter(edge => edge?.node?.id !== labelId)
        }),
      )
    }

    try {
      const response = await verifiedFetchJSON(`/${repositoryNameWithOwner}/labels/${labelId}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        // Rollback on failure
        if (queryKey && previousData) {
          queryClient.setQueryData(queryKey, previousData)
        }
        setErrorsPresent(true)
      }
    } catch {
      if (queryKey && previousData) {
        queryClient.setQueryData(queryKey, previousData)
      }
      setErrorsPresent(true)
    }
  }, [labelId, repositoryNameWithOwner, queryKey, queryClient, setIsDialogOpen, setErrorsPresent])
}
